
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConnectableObservable, Observable, Subject, Subscription } from 'rxjs';
import { catchError, finalize, flatMap, map, publishLast, publishReplay, refCount, switchAll, take, tap } from 'rxjs/operators';

import { HttpParams } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http/src/response';
import { empty } from 'rxjs/observable/empty';
import { _throw as throwError } from 'rxjs/observable/throw';
import { IID } from './IID';

/**
 * Array di items (ovvero rest objects cancellabili). Come il RESTObject, questo assume di essere
 * l'unico possessore dell'array. Quindi ha senso tenere in memoria qualcosa.
 *
 * L'url di questo oggetto è statico. Gli identificativi sono contenuti direttamente nel url.
 *
 * @export
 */
export class RESTArray<T extends R, R = any, ID = IID<string>> extends Observable<T[]> {

	protected generator: Subject<Observable<T[]>>;

	private disconnect: () => void;
	private connect: () => void;

	private readonly responseMapper = arr => arr.map(
		r => this.type(
			this.$http,
			`${this.uri}/${this.getUri(this.getID(r))}`,
			r
		)
	)

	constructor(
		protected readonly $http: HttpClient,
		protected readonly uri: string,
		protected readonly type: ($http: HttpClient, uri: string, copy: R) => T = (h, u, r) => <T>r,
		protected readonly getID: (t: R) => ID = (t: R) => <any>({ id: t['id'] }),
		protected readonly getUri: (id: ID) => string = (id) => `${id['id']}`,
		protected readonly manageQueryAuth: boolean = false
	) {
		super();
		this.generator = new Subject<Observable<T[]>>();

		const source = this.generator.asObservable().pipe(
			switchAll(),
			catchError((error) => {
				// Faccio una retry durante gli errori,
				// per non impallare il componente.
				//
				// @see Issue #6
				this.disconnect();
				// PAOLO: tento di bloccare la mitragliatrice irrazionale di richieste http
				// this.connect();
				return throwError(error);
			}),
			publishReplay(1)
		) as ConnectableObservable<T[]>;

		let connected: Subscription = null;

		this.disconnect = () => {
			if (connected) {
				connected.unsubscribe();
				connected = null;
			}
		};

		this.connect = () => {
			if (!connected) {
				connected = source.connect();
				this.refresh();
			}
		};

		this.source = source;

		const self = this;

		// Identity operator
		this.operator = function (s) {
			self.connect();
			return s.subscribe(this);
		};
	}

	// /**
	//  * Questo metodo serve a recuperare la
	//  * funzionalità dopo un errore.
	//  *
	//  * Sarebbe
	//  *
	//  * @memberof RESTArray
	//  */
	// public retry() {
	// 	this.disconnect();
	// 	this.connect();
	// }

	protected mapRequest(req: Observable<R[]>): Promise<T[]> {
		// Questo per sapere se la chiamata è andata a buon fine o no!
		return new Promise<T[]>((resolve, reject) => {
			const obs = req.pipe(
				map(this.responseMapper),
				// Metto la risoluzione in "do", di modo che non avvenga nessuna
				// subscription, ma che venga risolta quando serve.
				tap({
					next: resolve,
					error: err => reject(err)
				}),
				catchError((error: HttpErrorResponse) => {
					if (error.status === 403) {
						// Se non ho accesso a nulla, è inutile
						// rimanere connessi.
						this.disconnect();
						return empty();
					} else {
						return throwError(error);
					}
				}),
				// In finally faccio sempre reject. Se la promise è stata risolta
				// in precedenza, questa istruzione non ha effetto!
				// (quindi l'unico caso in cui questa chiamata ha effetto è
				// quando la richiesta viene cancellata)
				finalize(() => reject('Richiesta cancellata!'))
			) as Observable<T[]>;

			this.generator.next(obs);
		});
	}

	// FIXME: Questo è da rivedere.
	add(body: any) {
		const obs = this.pipe(
			take(1),
			flatMap(arr => {
				const newArr = arr.slice();
				return this.$http.post<R>(this.uri, body).pipe(
					map(res => {
						let uri = `${this.uri}/${this.getUri(this.getID(res))}`

						newArr.push(this.type(
							this.$http,
							uri,
							res
						));
						return newArr;
					})
				);
			}),
			publishLast(),
			refCount()
		);

		// Aggiungo la richiesta alla pipeline!
		this.generator.next(obs);

		// Questa sottoscrizione mi assicura che la chiamata non venga cancellata
		// per via di un unsubscribe lato switch
		return obs.toPromise();
	}

	refresh(options?: {
		headers?: HttpHeaders;
		params?: HttpParams;
		reportProgress?: boolean;
	}): Promise<R[]> {
		return this.mapRequest(
			this.$http.get<R[]>(this.uri, options)
		);
	}

	// FIXME: questo è da rivedere!
	delete(item: T, options?: {
		headers?: HttpHeaders;
		params?: HttpParams;
		reportProgress?: boolean;
	}): Promise<T[]> {
		const id = this.getID(item);
		const uri = this.getUri(id);

		// Calcolo la nuova array (come copia, per supportare la immutability)
		const next = this.pipe(
			take(1),
			map(arr =>
				arr.filter(t =>
					this.getUri(this.getID(t)) !== uri
				)
			)
		);

		// Chiamo la delete e al risultato mappo l'array attuale. Il risultato
		// verrà filtrato via, se necessario.
		const obs = this.$http
			.delete(this.uri, {
				// Questo da problemi se ID non è un object!
				params: Object.getOwnPropertyNames(id).reduce(
					(r, k) => r.set(k, id[k]),
					new HttpParams()
				),
				responseType: 'text',
				...options
			})
			.pipe(
				flatMap(() => next),
				publishLast(),
				refCount()
			);

		// Aggiungo la richiesta alla pipeline!
		this.generator.next(obs);

		// Questa sottoscrizione mi assicura che la chiamata non venga cancellata
		// per via di un unsubscribe lato switch
		return obs.toPromise();
	}
}



// WEBPACK FOOTER //
// ./src/app/angular-rest/RESTArray.ts